// Brand page (/brand/:id) and studio page (/studio/:id).

import React, { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import NavBar from "../../components/feature/NavBar";
import Footer from "../../components/feature/Footer";
import VideoCard from "../../components/feature/VideoCard";
import VideoQuickView from "../../components/feature/VideoQuickView";
import { useVideosWithEntities } from "../../hooks/useVideosWithEntities";
import { usePageMeta } from "../../hooks/usePageMeta";
import { useAuth } from "../../utils/AuthContext";
import { firstLogo } from "../../lib/catalog";
import { VideoThumbnail } from "../../types/cms";

function externalURL(link: string): string {
  return /^https?:\/\//i.test(link) ? link : `https://${link}`;
}

export default function EntityPage({ kind }: { kind: "companies" | "agencies" }) {
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const { videos, companies, agencies, loading } = useVideosWithEntities("public");
  const [openVideo, setOpenVideo] = useState<VideoThumbnail | null>(null);
  const isBrand = kind === "companies";

  const entity = useMemo(
    () => (isBrand ? companies : agencies).find((e) => e.id === id) ?? [...companies, ...agencies].find((e) => e.id === id),
    [isBrand, companies, agencies, id],
  );
  const entityVideos = useMemo(
    () => videos.filter((v) => (isBrand ? v.enterpriseId === id : v.agencyId === id)),
    [videos, isBrand, id],
  );

  usePageMeta(
    entity ? `${entity.name} motion design videos` : undefined,
    entity?.description || (entity ? `Motion design videos ${isBrand ? "made for" : "produced by"} ${entity.name} on The Motion Hub.` : undefined),
  );
  const close = useCallback(() => setOpenVideo(null), []);

  return (
    <div className="min-h-screen bg-neutral-50 text-gray-800 md:px-20 px-8">
      <NavBar />
      <VideoQuickView video={openVideo} onClose={close} />
      <div className="max-w-full w-full px-4 py-8 flex flex-col gap-8">
        {loading ? (
          <div className="animate-pulse flex gap-3 items-center">
            <div className="h-16 w-16 bg-gray-300 rounded" />
            <div className="h-6 w-48 bg-gray-300 rounded" />
          </div>
        ) : !entity ? (
          <div className="flex flex-col gap-3">
            <h1 className="text-3xl font-bold">{isBrand ? "Brand" : "Studio"} not found</h1>
            <Link to={isBrand ? "/brands" : "/studios"} className="underline">
              See all {isBrand ? "brands" : "studios"}
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-3 items-center">
                {firstLogo(entity) && (
                  <img src={firstLogo(entity)} alt={`${entity.name} logo`} className="h-16 w-16 object-contain" />
                )}
                <div>
                  <p className="text-sm text-neutral-500">{isBrand ? "Brand" : "Studio"}</p>
                  <h1 className="text-3xl font-bold">{entity.name}</h1>
                </div>
              </div>
              {isAdmin && (
                <Link to={`/admin/${isBrand ? "brand" : "studio"}/${entity.id}`} className="bg-black text-white text-sm px-4 py-2 rounded-full">
                  Modifier
                </Link>
              )}
            </div>
            {(entity.description || entity.websiteURL) && (
              <div className="flex flex-col items-start gap-3 p-6 bg-gray-100 rounded-2xl w-full">
                {entity.description && <p className="max-w-3xl">{entity.description}</p>}
                {entity.websiteURL && (
                  <a
                    href={externalURL(entity.websiteURL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-base rounded-lg border border-neutral-300 px-3 py-2 hover:bg-white"
                  >
                    Website <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
            <section className="flex flex-col gap-4">
              <h2 className="text-xl font-bold">
                {entityVideos.length} video{entityVideos.length !== 1 ? "s" : ""}
              </h2>
              {entityVideos.length === 0 ? (
                <p className="text-neutral-600">No published videos yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 gap-y-6">
                  {entityVideos.map((video) => (
                    <VideoCard key={video.id} video={video} onOpen={setOpenVideo} isAdmin={isAdmin} showBrand={!isBrand} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
